import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  SectionContainerTransparent,
  StyledTextInput,
} from './AuthCommonStyledComponents';
import { Button } from '../ui/Button';
import { useStore } from '@nanostores/react';
import { $authFlow } from '../store/auth';
import { autoSignIn, confirmSignIn, confirmSignUp } from 'aws-amplify/auth';
import type { ConfirmSignInInput, ConfirmSignUpInput } from '@aws-amplify/auth/src/providers/cognito/types/inputs';

type Payload = {
  confirmationCode: string
  username?: string
  options?: {}
}

export const VerificationForm: FC = () => {
  const authFlowStore = useStore($authFlow)
  const [otp, setOtp] = useState<string>("")
  const [invalidOtp, setInvalidOtp] = React.useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [email, setEmail] = useState<string | undefined>(undefined)
  const [verificationType, setVerificationType] = useState<'email' | 'signup'>("email")
  const [internalState, setInternalState] = useState<'init' | 'loaded'>('init')

  useEffect(() => {
    if (authFlowStore.state === 'confirm' && authFlowStore.persistentData.email) {
      setEmail(authFlowStore.persistentData.email)
      setVerificationType(authFlowStore.validationFrom as 'email' | 'signup')
      setInternalState('loaded')
    }
  }, [authFlowStore]);

  const onVerify = useCallback(async () => {
    if (!otp || otp.length === 0) {
      console.error("OTP code is required for sign in")
      return
    }
    try {
      setIsLoading(true)

      if (verificationType === 'email') {
        const payload: ConfirmSignInInput  ={
          challengeResponse: otp
        }
        const { nextStep: confirmSignInNextStep } = await confirmSignIn(payload)
        if (confirmSignInNextStep.signInStep === "DONE") {
          $authFlow.setKey('state', 'finish')
        }
      }
      if (verificationType === 'signup') {
        const payload: ConfirmSignUpInput = {
          username: email as string,
          confirmationCode: otp,
          options: {
            clientMetadata: {
              isConfirmingSignUp: "true"
            }
          }
        }
        const { nextStep: confirmSignUpNextStep } = await confirmSignUp(payload)
        if (confirmSignUpNextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
          const { nextStep } = await autoSignIn()
          if (nextStep.signInStep === "DONE") {
            $authFlow.setKey('state', 'finish-signup')
          }
        }
      }
    } catch (e) {
      console.error("Error during sign in challenge input:", JSON.stringify(e))
      // @ts-ignore
      if (e?.name === "CodeMismatchException") {
        setInvalidOtp(true)
      }
      return
    } finally {
      setIsLoading(false)
    }
  }, [verificationType, otp, email])

  return (
    <SectionContainerTransparent title={'Verify your account'}>
      <>
        <StyledTextInput
          label="Verification code"
          placeholder="Enter the code that we've sent to your email address..."
          onChange={otpChange => {
            setInvalidOtp(false)
            setOtp(otpChange)
          }}
          value={otp}
          required
          error={
            invalidOtp ? "Invalid verification code" : undefined
          }
          maxLength={8}
          transparent={false}
        />
        <Button
          large
          onClick={() => onVerify()}
          disabled={!otp || otp?.length === 0 || isLoading}
        >
          {`Confirm`}
        </Button>
      </>
    </SectionContainerTransparent>
  )
}